/**
 * useServiceOrdersDashboardOverview - dados da aba Visão Geral do Dashboard de O.S.
 * RELEASE: 2026-10-18 v1.0.0 - Uma única RPC agregadora (get_service_orders_dashboard_overview).
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export const SERVICE_ORDERS_DASHBOARD_QUERY_KEY = 'service-orders-dashboard-overview';

export interface ServiceOrdersDashboardFilters {
  startDate: Date;
  endDate: Date;
  serviceTypeId: string | null;
  providerId: string | null;
  categoryId: string | null;
}

export interface SoTopCustomer { rank: number; customer_id: string; customer_name: string; vehicles: number; orders: number }

export interface ServiceOrdersDashboardData {
  cards: {
    total_current: number; total_previous: number; today_current: number; today_previous: number;
    reschedule_now: number; reschedule_entered_current: number; reschedule_entered_previous: number;
    logistics_now: number; logistics_entered_current: number; logistics_entered_previous: number;
    on_time_current: number; evaluated_current: number; on_time_previous: number; evaluated_previous: number;
    exec_avg_current: number | null; exec_count_current: number; exec_avg_previous: number | null;
  };
  by_status: { status: string; value: number }[];
  sla: { late: number; at_risk: number; on_time: number; unscheduled: number };
  monthly: { month: string; total: number; by_type: Record<string, number> }[];
  by_type: { id: string | null; name: string; color: string | null; value: number }[];
  types: { id: string; name: string; color: string | null }[];
  top_customers_month: SoTopCustomer[];
  top_customers_year: SoTopCustomer[];
  /** v1.1.0: Qualidade da execução (O.S. criadas no período) */
  quality?: { reviewed: number; corrected: number; first_pass: number; cancelled: number; total: number; checklists_approved: number; checklists_total: number };
}

export function useServiceOrdersDashboardOverview(filters: ServiceOrdersDashboardFilters | undefined) {
  const { currentTenant } = useAuth();
  return useQuery({
    queryKey: [SERVICE_ORDERS_DASHBOARD_QUERY_KEY, currentTenant?.id, filters?.startDate?.toISOString(), filters?.endDate?.toISOString(),
      filters?.serviceTypeId, filters?.providerId, filters?.categoryId],
    enabled: !!currentTenant?.id && !!filters,
    queryFn: async () => {
      // `.call(supabase, ...)` preserva o receptor da RPC fora da tipagem gerada.
      const rpc = supabase.rpc as unknown as (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: Error | null }>;
      const { data, error } = await rpc.call(supabase, 'get_service_orders_dashboard_overview', {
        p_start_date: filters!.startDate.toISOString(),
        p_end_date: filters!.endDate.toISOString(),
        p_service_type_id: filters!.serviceTypeId,
        p_provider_person_id: filters!.providerId,
        p_category_id: filters!.categoryId,
        p_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo',
      });
      if (error) throw error;
      return data as ServiceOrdersDashboardData;
    },
    staleTime: 60_000,
  });
}
